import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ShippingPageCustomizationForm } from '../../../components/forms';
import {
  ChooseClaimLinkTemplateModal,
  ClaimLinkTemplateSelector,
  ClaimLinkTemplateSelectorActions,
} from '../../../components/_mui';
import { EXPERIENCE_ID } from '../../../constants/bucket';
import { ORGANIZATION_VALIDATION_SCHEMA as validationSchema } from '../../../constants/organizations';
import { useClaimLinkTemplate } from '../../../hooks/useClaimLinkTemplate';
import usePopover from '../../../hooks/usePopover';
import { selectCurrentOrganizationOptions } from '../../../store/selectors/organizations';
import { ICampaignCandidate } from '../../../types/campaigns';
import { ICampaignFormProps, MaintenanceFormStateEnum } from '../../../types/forms';
import { IClaimLinkTemplate } from '../../../types/templates';
import { hasLegacyClaimPageConfig } from '../../../utils/campaigns';
import { getRequiredFields } from '../../../utils/form';
import { normalizeExperienceId } from '../../../utils/templates';

export interface ICampaignClaimPageStepProps extends ICampaignFormProps<ICampaignCandidate> {
  className?: string;
}

const CampaignClaimPageStep: React.FC<ICampaignClaimPageStepProps> = ({
  form,
  className,
  mode,
  onChange,
  readOnly,
}) => {
  const requiredFields = useMemo(() => getRequiredFields(validationSchema), []);

  const { isOpen, handleOpen: handleOpenModal, handleClose: handleCloseModal } = usePopover();

  const orgOptions = useSelector(selectCurrentOrganizationOptions);

  const orgDefaultExperienceId = useMemo(() => normalizeExperienceId(orgOptions?.[EXPERIENCE_ID]), [orgOptions]);
  const campaignExperienceId = useMemo(() => normalizeExperienceId(form.values[EXPERIENCE_ID]), [form.values]);

  const selectedTemplate = useClaimLinkTemplate(campaignExperienceId);

  const isTemplateLoading = useMemo(
    () => !!campaignExperienceId && !selectedTemplate,
    [campaignExperienceId, selectedTemplate],
  );

  const hasLegacyConfig = useMemo(() => hasLegacyClaimPageConfig(form.values), [form.values]);

  const shouldPreSelectOrgDefault = useMemo(
    () => !readOnly && !!orgDefaultExperienceId && !campaignExperienceId && !hasLegacyConfig,
    [readOnly, orgDefaultExperienceId, campaignExperienceId, hasLegacyConfig],
  );

  useEffect(() => {
    if (shouldPreSelectOrgDefault && onChange) {
      onChange(EXPERIENCE_ID, orgDefaultExperienceId ?? undefined);
    }
  }, [shouldPreSelectOrgDefault, orgDefaultExperienceId, onChange]);

  const handleSelectTemplate = useCallback(
    (template: IClaimLinkTemplate) => {
      if (onChange) {
        onChange(EXPERIENCE_ID, template.uid);
      }
    },
    [onChange],
  );

  const handleResetToOrgDefault = useCallback(() => {
    if (orgDefaultExperienceId && onChange) {
      onChange(EXPERIENCE_ID, orgDefaultExperienceId);
    }
  }, [orgDefaultExperienceId, onChange]);

  const handleRemoveTemplate = useCallback(() => {
    if (onChange) {
      onChange(EXPERIENCE_ID, undefined);
    }
  }, [onChange]);

  const hasTemplateSelected = useMemo(() => !!campaignExperienceId, [campaignExperienceId]);

  const isSelectedTemplateOrgDefault = useMemo(
    () => campaignExperienceId === orgDefaultExperienceId,
    [campaignExperienceId, orgDefaultExperienceId],
  );

  const canResetToDefault = useMemo(
    () => !!orgDefaultExperienceId && hasTemplateSelected && !isSelectedTemplateOrgDefault,
    [orgDefaultExperienceId, hasTemplateSelected, isSelectedTemplateOrgDefault],
  );

  const showRemoveTemplate = useMemo(
    () => mode === MaintenanceFormStateEnum.Editing && hasLegacyConfig && hasTemplateSelected,
    [mode, hasLegacyConfig, hasTemplateSelected],
  );

  const templateActions = useMemo(
    () =>
      canResetToDefault || showRemoveTemplate ? (
        <ClaimLinkTemplateSelectorActions
          onReset={canResetToDefault ? handleResetToOrgDefault : undefined}
          onChangeTemplate={handleOpenModal}
          onRemove={showRemoveTemplate ? handleRemoveTemplate : undefined}
          disabled={readOnly}
        />
      ) : undefined,
    [canResetToDefault, showRemoveTemplate, handleResetToOrgDefault, handleRemoveTemplate, handleOpenModal, readOnly],
  );

  const templateSelector = useMemo(
    () => (
      <ClaimLinkTemplateSelector
        title="Choose a Claim Page template"
        subtitle="Pick a template to control the claim page layout and branding"
        selectedTemplate={selectedTemplate}
        onSelect={handleOpenModal}
        disabled={readOnly}
        loading={isTemplateLoading}
        actions={templateActions}
      />
    ),
    [selectedTemplate, handleOpenModal, readOnly, isTemplateLoading, templateActions],
  );

  return (
    <div className={className}>
      {templateSelector}
      {hasLegacyConfig && (
        <ShippingPageCustomizationForm
          form={form}
          readOnly={readOnly}
          onChange={onChange}
          requiredFields={requiredFields}
          hasTemplateSelected={hasTemplateSelected}
        />
      )}
      <ChooseClaimLinkTemplateModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        onSelect={handleSelectTemplate}
        currentTemplateId={campaignExperienceId ?? undefined}
        title="Choose a Claim Page template"
      />
    </div>
  );
};

export default CampaignClaimPageStep;
